import { RouteComponentProps, useParams } from '@reach/router'
import { Button, Form, Input, InputNumber, message, Radio, Row, Col, Select } from 'antd'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { useLoading, useMedia } from '../common/hooks'
import { required, validateMessages } from '../common/validate'
import { PaymentInterval } from '../components/PaymentInterval'
import { Ad, Title } from '../components/ui'
import { getPaymentInfo, listingPayment, PaymentInfo, preflight, ReqPayment } from '../services/payment'
import Fpx from '../assets/payment1.png'
import EWallet from '../assets/payment2.png'

const StyledPayment = styled.div`
  input,
  .ant-select {
    max-width: 300px;
  }

  .scroll-item {
    height: 320px;
    color: #fff;
    line-height: 160px;
    text-align: center;
    background: #364d79;
  }

  .ant-form-item-label {
    margin-right: 1em;
  }
`
const { Option } = Select
const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
}
const tailLayout = {
  wrapperCol: { offset: 8, span: 16 },
}

const usePaymentList = (ano: string, setAno: React.Dispatch<any>) => {
  const [paymentList, setPaymentList] = useState<string[]>([])

  useEffect(() => {
    const fetchPaymentList = async () => {
      const list = await listingPayment()
      setPaymentList(list)
      if (list.length > 0 && Number(ano) === 0) {
        setAno(list[0])
      }
    }
    fetchPaymentList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return paymentList
}

export const Payment: React.FC<RouteComponentProps> = () => {
  const { ano: anoQuery } = useParams()
  const [form] = Form.useForm()
  const [method, setMethod] = useState<'bank' | 'eWallet'>('bank')
  const [fee, setFee] = useState('0')
  const [ano, setAno] = useState(anoQuery)
  const [_refNo, setRefNo] = useState('')
  const { loading, start, finish } = useLoading()
  const [pay, setPay] = useState(0)
  const total = useMemo(() => (pay * 100 + Number(fee) * 100) / 100, [pay, fee])
  const [paymentResultVisible, setPaymentResultVisible] = useState(false)
  const intervalRef = useRef(0)
  const [remaining, setRemaining] = useState(1000 * 60 * 5)
  const paymentList = usePaymentList(ano, setAno)
  const isMobile = useMedia('(max-width: 800px)')
  const [data, setData] = useState<PaymentInfo>({
    prodDesc: '',
    email: '',
    ewallet: [],
    paymentAmount: '',
    ano: '',
    mobile: '',
    name: '',
    fpx: [],
  } as PaymentInfo)

  useEffect(() => {
    const fetchInfo = async () => {
      const d = await getPaymentInfo(ano)
      form.setFieldsValue({ email: d.email })
      setPay(Number(d.paymentAmount))
      setData(d)
    }
    fetchInfo()
  }, [ano, form])

  // const { data } = useSWR(
  //   '/customer/acc/payment/info',
  //   async () => {
  //     const data = await getPaymentInfo(ano)
  //     form.setFieldsValue({ email: data.email })
  //     setPay(Number(data.paymentAmount))
  //     return data
  //   },
  //   { refreshInterval: 0, revalidateOnFocus: false, revalidateOnReconnect: false }
  // )

  const selectFPX = (fpx: string) => {
    const item = data?.fpx.find((item) => item.paymentId === fpx)
    if (item) {
      setFee(item.fee)
    }
  }
  const selectPaymentNo = (no: string) => {
    setAno(no)
  }
  const selectWallet = (ewallet: string) => {
    const item = data?.ewallet.find((item) => item.paymentId === ewallet)
    if (item) {
      setFee(item.fee)
    }
  }

  const onPaymentChange = (value?: number | string) => {
    setPay(Number(value) || 0)
    form.setFieldsValue({ amount: value })
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const startInterval = () => {
    intervalRef.current = setInterval(() => {
      if (remaining <= 0) {
        clearImmediate(intervalRef.current)
        setRemaining(1000 * 60 * 5)
        setPaymentResultVisible(false)
        message.warning('payment timeout')
      } else {
        setRemaining((s) => s - 1)
      }
    }, 1000)
  }

  const submit = async () => {
    start()
    try {
      await form.validateFields()
    } catch (error) {
      console.error(error)
      finish()
      return
    }
    try {
      const paymentId = String(form.getFieldValue('fpx') | form.getFieldValue('ewallet'))
      const email = form.getFieldValue('email')
      // const amount = Number(form.getFieldValue('amount')).toFixed(2)
      const { refNo, code, errorMessage, backendURL, signature, signatureType, merchantCode, responseURL, totleAmount } = await preflight({
        phone: data?.mobile!,
        email,
        totleAmount: total.toFixed(2),
        actualCurrency: pay.toFixed(2),
        poundage: Number(fee).toFixed(2),
        userName: data?.name!,
        orderNo: ano,
        paymentId,
        channel: 'Portal',
      })
      setRefNo(refNo)

      if (code !== 200) {
        message.error(errorMessage)
        finish()
        return
      }

      const params = {
        MerchantCode: merchantCode,
        PaymentId: paymentId,
        RefNo: refNo,
        Amount: totleAmount,
        Currency: 'MYR',
        ProdDesc: data?.prodDesc!,
        UserName: data?.name!,
        UserEmail: email,
        UserContact: data?.mobile!,
        Remark: '',
        Lang: 'UTF-8',
        SignatureType: signatureType,
        Signature: signature,
        // ResponseURL: `${window.location.origin}/dashboard/payment/result?refNo=${refNo}`,
        ResponseURL: responseURL,
        BackendURL: backendURL,
      }
      // The rest of this code assumes you are not using a library.
      // It can be made less wordy if you use one.
      const formEl = document.createElement('form')
      formEl.method = 'POST'
      formEl.action = 'https://payment.ipay88.com.my/epayment/entry.asp'
      // formEl.target = '_blank'

      for (const key in params) {
        if (params.hasOwnProperty(key)) {
          const hiddenField = document.createElement('input')
          hiddenField.type = 'hidden'
          hiddenField.name = key
          hiddenField.value = params[key as keyof ReqPayment]

          formEl.appendChild(hiddenField)
        }
      }

      document.body.appendChild(formEl)
      formEl.submit()
      // setPaymentResultVisible(true)
      // startInterval()
    } catch (error) {
      console.error(error)
      finish()
    }
  }

  useEffect(() => {
    return () => {
      clearImmediate(intervalRef.current)
    }
  })
  return (
    <StyledPayment>
      <div className="flex bg-gray-200 p-10 pt-15 mb-5 flex-col sm:flex-row">
        <div className="flex lg:mr-16 justify-center">
          <img className="h-14 mr-2" src={Fpx} alt="fpx" />
          <img className="h-14" src={EWallet} alt="ewallet" />
        </div>
        <div>
          <Title className="my-5 sm:my-0">Payment</Title>
          <p className="text-lg">It has never been easier for you to pay your instalment with us.</p>
        </div>
      </div>
      <div className="flex flex-col lg:flex-row justify-items-center align-center">
        <div className="m-auto lg:m-0 lg:mr-5" style={isMobile ? { width: '90%' } : { width: '400px' }}>
          <Ad />
        </div>
        <Form className="sm:pl-20 mt-10 lg:mt-0" form={form} validateMessages={validateMessages} initialValues={{ email: data?.email, method }}>
          <Form.Item {...layout} label="Agreement Number">
            {paymentList.length <= 1 ? (
              ano
            ) : (
              <Select onChange={selectPaymentNo} value={ano}>
                {paymentList.map((item) => (
                  <Option key={item} value={item}>
                    {item}
                  </Option>
                ))}
              </Select>
            )}
          </Form.Item>
          <Form.Item {...layout} label="Amount Due">
            {data?.paymentAmount} RM
          </Form.Item>
          <Form.Item {...layout} name="amount" label="Payment Amount" rules={[required]}>
            <div>
              <InputNumber precision={2} min={1} onChange={onPaymentChange} /> RM
            </div>
          </Form.Item>
          <Form.Item
            {...layout}
            name="email"
            label="Email Address"
            rules={[
              required,
              {
                type: 'email',
                message: 'email is not valid',
              },
            ]}
          >
            <Input type="email" />
          </Form.Item>
          <Form.Item {...layout} label="Payment Method" name="method" rules={[required]}>
            <Radio.Group className="flex items-center" value={method} onChange={(m) => setMethod(m.target.value)}>
              <img className="h-4 inline mr-1" src={Fpx} alt="fpx" /> <Radio value="bank">Online Banking - FPX</Radio>
              <img className="h-4 inline mr-1" src={EWallet} alt="ewallet" />
              <Radio value="eWallet">eWallet</Radio>
            </Radio.Group>
          </Form.Item>
          {method === 'bank' && (
            <Form.Item {...layout} label="Select Bank" name="fpx" rules={[required]}>
              <Select onChange={selectFPX}>
                {data?.fpx.map((item) => (
                  <Option key={item.paymentId} value={item.paymentId}>
                    {item.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
          {method === 'eWallet' && (
            <Form.Item {...layout} label="Select eWallet" name="ewallet" rules={[required]}>
              <Select onChange={selectWallet}>
                {data?.ewallet.map((item) => (
                  <Option key={item.paymentId} value={item.paymentId}>
                    {item.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
          {/* <Form.Item {...layout} label="Processing Fee">
            {fee} RM
          </Form.Item> */}
          <Form.Item {...layout} label="Total Payment Amount">
            {total.toFixed(2)} RM
          </Form.Item>

          {/* {method === 'bank' && (
          <Form.Item
            {...tailLayout}
            name="terms-bank"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) => (value ? Promise.resolve() : Promise.reject('Should accept agreement')),
              },
            ]}
          >
            <Checkbox>
              I agree to the terms and conditions of Shenma Credit Sdn Bhd and also FPX's terms and conditions
              <br />
              Note: You will be redirected securely to the Bank Online Banking website, where you will be asked to enter your username and password at the Bank website.
            </Checkbox>
          </Form.Item>
        )}
        {method === 'eWallet' && (
          <Form.Item
            {...tailLayout}
            name="agreement"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) => (value ? Promise.resolve() : Promise.reject('Should accept agreement')),
              },
            ]}
          >
            <Checkbox>
              I agree to the terms and conditions of Shenma Credit Sdn Bhd.
              <br />
              Note: Please scan the QR Code displayed on your screen using your e-Wallet app.
            </Checkbox>
          </Form.Item>
        )} */}

          <Form.Item {...(isMobile ? {} : tailLayout)}>
            <Row>
              <Col span={12}>
                <Button className="w-32" type="primary" danger onClick={() => window.history.back()}>
                  Back
                </Button>
              </Col>
              <Col span={12}>
                <Button className="w-32" type="primary" onClick={submit} loading={loading}>
                  Continue
                </Button>
              </Col>
            </Row>
          </Form.Item>
        </Form>
      </div>
      {paymentResultVisible && _refNo ? <PaymentInterval ano={ano} refNo={_refNo} /> : null}
    </StyledPayment>
  )
}
